import { useSelector } from 'react-redux';

import { createGraphMetadata, useSite } from '@labradorsports/utils';

import { PlayEditor } from '../../play-editor/index.js';
import { HelmetWrapper, InlineLoadingSpinner } from '../../shared/index.js';
import { NavigateWithQuery } from '../../shared/structural/index.js';
import {
    useAddCommunityPlayCommentMutation,
    useGetCommunityPlayQuery,
    useGetCommunityPlayCommentsQuery,
    RouterSelectors,
} from '../../store/index.js';
import CommunityPlayComments from '../community-play-comments/community-play-comments.js';
import CommunityPlayDetails from '../community-play-details/community-play-details.js';

const CommunityPlay: FC = () => {
    const { Config, PlayConfig } = useSite();
    const [addCommunityPlayComment] = useAddCommunityPlayCommentMutation();
    const openPlayId = useSelector(RouterSelectors.communityPlayId);
    const { data: openPlay } = useGetCommunityPlayQuery({ id: openPlayId });
    const { data: playComments, isFetching: commentsFetching } = useGetCommunityPlayCommentsQuery({ id: openPlayId });
    const { PlayTypes, FieldDescriptor } = PlayConfig;

    if (!openPlayId) {
        return <NavigateWithQuery to="/community" />;
    }

    if (!openPlay) {
        return <InlineLoadingSpinner />;
    }

    const onAddComment = (text: string) => {
        addCommunityPlayComment({ id: openPlayId, text });
    };

    const canonical = `${Config.SiteConfig.Url}/community/play?p=${openPlay.id}`;

    const PlayTypeDisplay = {
        [PlayTypes.HALF_FIELD]: `Half-${FieldDescriptor}`,
        [PlayTypes.FULL_FIELD]: `Full-${FieldDescriptor}`,
        [PlayTypes.DRILL]: 'Drill',
    };

    const breadcrumbTag = openPlay.tags?.[0] ?? PlayTypeDisplay[openPlay.type];

    const breadcrumbs = [
        {
            name: Config.SiteConfig.SiteName,
            url: Config.SiteConfig.Url,
        },
        {
            name: 'Community Playbook',
            url: `${Config.SiteConfig.Url}/community`,
        },
        {
            name: breadcrumbTag,
            url: `${Config.SiteConfig.Url}/community/search?q=${breadcrumbTag}`,
        },
        {
            name: openPlay.name,
            url: canonical,
        },
    ];

    return (
        <div className="community-play">
            <HelmetWrapper
                title={`${openPlay.name} - Community Playbook`}
                description={`${openPlay.description} ${openPlay.tags.join(', ')} - animated play diagram`}
                image={openPlay.previewUrl}
            >
                <link rel="canonical" href={canonical} />
                <meta property="og:url" content={canonical} />

                <script type="application/ld+json">{createGraphMetadata(breadcrumbs)}</script>
            </HelmetWrapper>
            <div className="community-play-content">
                <div className="play-editor-container">
                    <PlayEditor readOnly />
                </div>
                <div className="play-details-container">
                    <CommunityPlayDetails play={openPlay} />
                </div>
            </div>
            {commentsFetching ? (
                <InlineLoadingSpinner />
            ) : (
                <CommunityPlayComments comments={playComments} addComment={onAddComment} />
            )}
        </div>
    );
};

export default CommunityPlay;
